import { MergeFieldDto } from '@lmt/shared-types';
import { getMergeFields } from '@lmt/unicorn-shared/app/components/documents/expressions/data-cache';
import { QueryClient } from '@tanstack/react-query';
import {
  distinctQueries,
  findQueries,
  QueryWithParameters,
  setSelectVisitsFunction,
} from '@lmt/expressions';

export async function extractUniqueQueriesFromDocument(
  queryClient: QueryClient,
  docVariables: string[],
  selectVisitsFunction: () => Promise<number[] | null>,
): Promise<QueryWithParameters[]> {
  const mergeFields = await getMergeFields(queryClient);
  let selectedVisitsPromise: Promise<number[]> | undefined;
  setSelectVisitsFunction(async () => {
    if (!selectedVisitsPromise)
      selectedVisitsPromise = selectVisitsFunction().then((x) => x ?? []);
    return selectedVisitsPromise;
  });

  const uniqueQueriesPromises = docVariables.map(async (variableName) => {
    const mergeField = getMergeFieldByVariableName(mergeFields, variableName);
    const expression = mergeField?.expression || variableName;

    try {
      const queriesForVariable = await findQueries(expression);

      return queriesForVariable;
    } catch (e) {
      console.error('Error processing expression', variableName, e);
      return [] as QueryWithParameters[];
    }
  });

  const uniqueQueriesPerVariable = await Promise.all(uniqueQueriesPromises);
  const allQueries = ([] as QueryWithParameters[]).concat(
    ...uniqueQueriesPerVariable,
  );

  return distinctQueries(allQueries);
}

export function getMergeFieldByVariableName(
  mergeFields: MergeFieldDto[],
  variableName: string,
): MergeFieldDto | null {
  const mergeFieldId =
    extractMergeFieldPrefixAndCodeFromVariableName(variableName);

  return (
    mergeFields.find(
      (x) => x.code === mergeFieldId?.code && x.prefix === mergeFieldId?.prefix,
    ) ?? null
  );
}

export function extractMergeFieldPrefixAndCodeFromVariableName(
  variableName: string,
): { prefix: string; code: number } | null {
  const matchResult = variableName.match(/^(?<prefix>.*?)(?<code>\d+)$/);
  if (!matchResult) return null;

  const code = parseInt(matchResult?.groups?.code ?? '');
  if (isNaN(code)) return null;
  const prefix = matchResult?.groups?.prefix;
  if (!prefix) return null;

  return { prefix, code };
}
